import type { OperationProgress } from "../../types";
import type { AmlogicDeviceSession } from "./AmlogicDeviceSession";
import type { AmlogicSessionManager } from "./AmlogicSessionManager";
import type { AmlogicJobScheduler } from "./AmlogicJobScheduler";

export type AmlogicBatchEventType =
  | "queued"
  | "running"
  | "progress"
  | "completed"
  | "cancelled"
  | "failed";

export interface AmlogicBatchEvent {
  batchId: string;
  type: AmlogicBatchEventType;
  session: AmlogicDeviceSession;
  jobId: string;
  connectionEpoch: number;
  progress?: OperationProgress;
  error?: unknown;
}

export interface AmlogicBatchExecutionContext {
  readonly signal: AbortSignal;
  readonly jobId: string;
  reportProgress(progress: OperationProgress): void;
}

export interface AmlogicBatchRequest {
  batchId?: string;
  kind: string;
  activity: string;
  targets: readonly AmlogicDeviceSession[];
  execute(session: AmlogicDeviceSession, context: AmlogicBatchExecutionContext): Promise<void>;
  onEvent?(event: AmlogicBatchEvent): void;
}

export interface AmlogicBatchJobHandle {
  readonly jobId: string;
  readonly sessionId: string;
  readonly completion: Promise<void>;
}

export interface AmlogicBatchHandle {
  readonly batchId: string;
  readonly jobs: readonly AmlogicBatchJobHandle[];
  readonly settled: Promise<PromiseSettledResult<void>[]>;
}

interface ActiveBatchRegistry {
  readonly jobIds: Set<string>;
  readonly sessionIds: Set<string>;
  pendingJobs: number;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Production batch lifecycle shared by the UI and fake-transport integration
 * tests. Protocol work remains injectable through `execute`, while ownership,
 * cancellation, session state, progress and scheduler isolation stay real.
 */
export class AmlogicBatchRunner {
  private readonly unsubscribe: () => void;
  private readonly disconnected = new Set<string>();
  private readonly batchJobs = new Map<string, ActiveBatchRegistry>();

  constructor(
    private readonly manager: AmlogicSessionManager,
    private readonly scheduler: AmlogicJobScheduler,
  ) {
    this.unsubscribe = manager.subscribe(() => this.cancelDisconnectedJobs());
  }

  dispose(): void {
    this.unsubscribe();
  }

  runBatch(request: AmlogicBatchRequest): AmlogicBatchHandle {
    const batchId = request.batchId ?? crypto.randomUUID();
    if (this.batchJobs.has(batchId)) throw new Error(`Batch ${batchId} is already active.`);
    this.batchJobs.set(batchId, { jobIds: new Set(), sessionIds: new Set(), pendingJobs: 0 });
    const jobs = request.targets.map((session) => this.enqueueTarget(request, session, batchId));
    const settled = Promise.allSettled(jobs.map((job) => job.completion));
    if (jobs.length === 0) this.batchJobs.delete(batchId);
    return {
      batchId,
      jobs,
      // Attach rejection handlers immediately even when the React caller does
      // not need to await the entire batch.
      settled,
    };
  }

  /** Adds a device connected after Start to the still-running batch. */
  appendTarget(request: AmlogicBatchRequest, session: AmlogicDeviceSession): AmlogicBatchJobHandle | undefined {
    const batchId = request.batchId;
    if (!batchId) throw new Error("A batchId is required when appending a target.");
    const registry = this.batchJobs.get(batchId);
    if (!registry) return undefined;
    if (registry.sessionIds.has(session.id)) return undefined;
    return this.enqueueTarget(request, session, batchId);
  }

  /** Cancels only jobs owned by one full-package panel. */
  cancelBatch(batchId: string): number {
    const registry = this.batchJobs.get(batchId);
    if (!registry) return 0;
    let cancelled = 0;
    for (const jobId of registry.jobIds) {
      if (this.scheduler.cancelJob(jobId)) cancelled += 1;
    }
    return cancelled;
  }

  private enqueueTarget(request: AmlogicBatchRequest, session: AmlogicDeviceSession, batchId: string): AmlogicBatchJobHandle {
    const registry = this.batchJobs.get(batchId);
    if (!registry) throw new Error(`Batch ${batchId} is no longer active.`);
    const jobId = crypto.randomUUID();
    registry.jobIds.add(jobId);
    registry.sessionIds.add(session.id);
    registry.pendingJobs += 1;
    const currentEpoch = () => this.manager.getSession(session.id)?.connectionEpoch ?? session.connectionEpoch;
    let terminalEmitted = false;
    const emit = (type: AmlogicBatchEventType, extra?: Pick<AmlogicBatchEvent, "progress" | "error">) => {
      if (type === "completed" || type === "cancelled" || type === "failed") terminalEmitted = true;
      request.onEvent?.({
        batchId,
        type,
        session,
        jobId,
        connectionEpoch: currentEpoch(),
        ...extra,
      });
    };

    this.manager.setSessionJobStatus(session.id, "queued", jobId, undefined, currentEpoch());
    this.manager.pushSessionLog(session.id, `${request.activity} · trong hàng đợi`, currentEpoch(), { batchId, jobId });
    emit("queued");

    const scheduled = this.scheduler.enqueueJob({
      jobId,
      sessionId: session.id,
      kind: request.kind,
      cancelRunning: () => session.service.cancelCurrentOperation(),
      run: async (signal) => {
        this.manager.setSessionJobStatus(session.id, "running", jobId, undefined, currentEpoch());
        this.manager.pushSessionLog(session.id, `${request.activity} · bắt đầu`, currentEpoch(), { batchId, jobId });
        emit("running");
        try {
          await request.execute(session, {
            signal,
            jobId,
            reportProgress: (progress) => {
              // A transport callback may arrive after its job has already
              // completed or after the session has moved to another job.
              // Keep progress/log/batch state owned by the active job only.
              if (signal.aborted || this.manager.getSession(session.id)?.activeJobId !== jobId) return;
              const epoch = currentEpoch();
              this.manager.updateSessionProgress(session.id, progress, epoch);
              this.manager.upsertSessionProgressLog(session.id, progress, epoch, { batchId, jobId });
              emit("progress", { progress });
            },
          });
          if (signal.aborted) {
            this.markCancelled(session, currentEpoch(), batchId, jobId);
            emit("cancelled");
            return;
          }
          this.manager.setSessionJobStatus(session.id, "completed", undefined, undefined, currentEpoch());
          this.manager.pushSessionLog(session.id, `${request.activity} · hoàn tất`, currentEpoch(), { batchId, jobId });
          emit("completed");
        } catch (error) {
          if (signal.aborted) {
            this.markCancelled(session, currentEpoch(), batchId, jobId);
            emit("cancelled", { error });
          } else {
            const detail = errorMessage(error);
            this.manager.setSessionJobStatus(session.id, "failed", undefined, detail, currentEpoch());
            this.manager.pushSessionLog(session.id, `${request.activity} · lỗi · ${detail}`, currentEpoch(), { batchId, jobId });
            emit("failed", { error });
          }
          throw error;
        }
      },
    });
    const completion = scheduled.catch((error) => {
      // A queued job never enters `run`, so cancellation must still update the
      // owning panel and session lifecycle here.
      if (!terminalEmitted && this.scheduler.getJob(jobId)?.state === "cancelled") {
        this.markCancelled(session, currentEpoch(), batchId, jobId);
        emit("cancelled", { error });
      }
      throw error;
    });
    const finishRegistryJob = () => {
      const current = this.batchJobs.get(batchId);
      if (!current) return;
      current.pendingJobs = Math.max(0, current.pendingJobs - 1);
      if (current.pendingJobs === 0) this.batchJobs.delete(batchId);
    };
    void completion.then(finishRegistryJob, finishRegistryJob);

    return { jobId, sessionId: session.id, completion };
  }

  private markCancelled(session: AmlogicDeviceSession, epoch: number, batchId: string, jobId: string): void {
    // A physical disconnect is more informative than the derived job state.
    // Keep it visible while still allowing the scheduler to finish as cancelled.
    if (this.manager.getSession(session.id)?.status === "disconnected") return;
    this.manager.setSessionJobStatus(session.id, "cancelled", undefined, undefined, epoch);
    this.manager.pushSessionLog(session.id, "Đã hủy job", epoch, { batchId, jobId });
  }

  private cancelDisconnectedJobs(): void {
    for (const session of this.manager.getSnapshot()) {
      if (session.status !== "disconnected") {
        this.disconnected.delete(session.id);
        continue;
      }
      if (this.disconnected.has(session.id)) continue;
      this.disconnected.add(session.id);
      this.scheduler.cancelSession(session.id);
    }
  }
}
